import { useState } from "react";
import FixedVideoBackground from "@/components/homepage/FixedVideoBackground";
import { httpClient } from "@/api/http-client";

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");

    try {
      await httpClient.post("/contact", form);
      setStatus("success");
      setForm({ name: "", email: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <>
      <FixedVideoBackground />

      <div className="relative min-h-screen py-16 space-y-16">
        <div className="max-w-[1600px] mx-auto px-0 sm:px-12">
          <section className="relative bg-white py-16 sm:py-24 overflow-hidden shadow-2xl rounded-none sm:rounded-3xl">
            <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6">
              <p className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-4 text-center">
                Get in Touch
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-4 text-center">
                Contact Us
              </h1>
              <p className="text-lg text-slate-600 font-semibold mb-10 text-center">
                Have a question or want to discuss how AI can transform your
                business? Send us a message and we'll get back to you.
              </p>

              {status === "success" && (
                <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 font-semibold text-center">
                  Your message has been sent! We'll get back to you soon.
                </div>
              )}

              {status === "error" && (
                <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800 font-semibold text-center">
                  Something went wrong. Please try again or email us directly at{" "}
                  <a
                    href="mailto:noah@aigeniusautomations.com"
                    className="underline hover:text-red-900"
                  >
                    noah@aigeniusautomations.com
                  </a>
                  .
                </div>
              )}

              {status !== "success" && (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-semibold text-slate-700 mb-2"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      maxLength={100}
                      value={form.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 font-medium focus:outline-none focus:border-[#B87333] transition-colors"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold text-slate-700 mb-2"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 font-medium focus:outline-none focus:border-[#B87333] transition-colors"
                      placeholder="you@example.com"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-semibold text-slate-700 mb-2"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={5000}
                      rows={5}
                      value={form.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl bg-white text-slate-900 font-medium focus:outline-none focus:border-[#B87333] transition-colors resize-none"
                      placeholder="How can we help?"
                    />
                    <p className="text-xs text-slate-400 mt-1 text-right">
                      {form.message.length}/5000
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full py-4 bg-[#B87333] text-white font-semibold rounded-xl hover:bg-[#A66329] transition-colors shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "submitting" ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}

              {status === "success" && (
                <div className="text-center">
                  <button
                    onClick={() => setStatus("idle")}
                    className="text-[#B87333] font-semibold hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              )}

              <div className="mt-12 pt-8 border-t border-slate-200 text-center">
                <p className="text-slate-600 font-semibold mb-4">
                  Want to book a call instead?
                </p>
                <a
                  href="https://calendar.notion.so/meet/aigeniusconsulting/793ez04oz7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white font-semibold rounded-xl hover:bg-slate-800 transition-colors shadow-lg"
                >
                  Book a Strategy Call
                  <svg
                    className="w-5 h-5 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default ContactPage;
